import {
  Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild, OnChanges, SimpleChanges
} from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { isEmpty } from 'lodash-es';
import { BehaviorSubject, map } from 'rxjs';

export interface InlineSearchItem<T = unknown> {
  img?: string;
  title: string;
  subtitle?: string;
  badge?: {
    text: string;
    color: string;
  }
  fuseJsHighlighted?: {
    title: string;
  };
  id: string;
  rawObject: T;
}

export type InlineSearchFunction = (query: string) => Promise<InlineSearchItem[]>;

@Component({
  selector: 'me-inline-search',
  templateUrl: './inline-search.component.html',
  styleUrls: ['./inline-search.component.scss'],
})
export class InlineSearchComponent implements OnInit, OnChanges {
  @Input() searchFn: InlineSearchFunction;

  @Input() ionItemFill: string;

  @Input() searchLabel: string;

  @Input() placeholder: string;

  @Input() ionLabelPosition: string;

  /** Will be used to exclude items already selected */
  @Input() excludeItemIds: undefined | null | Set<string> = new Set<string>();

  @Input() disabled: boolean;

  @Input() showAllOnFocus = false;

  @Output() itemSelected = new EventEmitter<InlineSearchItem<any>>();

  @Input() searchHelperText?: string;

  @HostListener('keydown.Escape', ['$event'])
  @HostListener('document:keydown.Escape', ['$event'])
  @HostListener('window:keydown.Escape', ['$event']) onEscapePress(event: KeyboardEvent) {
    const autoCompleteListPresent = !this.hideAutoCompleteItems$.value;
    if (event && autoCompleteListPresent) {
      event.preventDefault();
      event.stopPropagation();

      this.hideAutoCompleteItems();
    }
  }

  @HostListener('keydown.Enter', ['$event'])
  @HostListener('document:keydown.Enter', ['$event'])
  @HostListener('window:keydown.Enter', ['$event'])
  onEnterPress(event: KeyboardEvent) {
    const autoCompleteListPresent = !this.hideAutoCompleteItems$.value;

    if (event && autoCompleteListPresent) {
      const selectedItem = this.visibleItems$.value[this.activeSearchItemIndex];

      if (!selectedItem) {
        return;
      }

      // Select the focused item
      this.selectItem(selectedItem);

      event.preventDefault();
      event.stopPropagation();
    }
  };


  // If there is a click outside the component, hide the autocomplete list
  @HostListener('document:click', ['$event']) onDocumentClick(event: MouseEvent) {
    const autoCompleteListPresent = !this.hideAutoCompleteItems$.value;
    if (autoCompleteListPresent) {
      const target = event.target as HTMLElement;
      const isInsideComponent = this.elementRef.nativeElement.contains(target);
      if (!isInsideComponent) {
        this.hideAutoCompleteItems();
      }
    }
  }

  onBlur(event: Event & { detail?: FocusEvent }) {
    // We only want to hide the autocomplete list if the user clicks outside the component, a blur caused by the
    // ion-items shouldnt result in the autocomplete list being hidden
    const autoCompleteListPresent = !this.hideAutoCompleteItems$.value;
    if (autoCompleteListPresent) {
      // https://stackoverflow.com/questions/54489244/ionblur-event-detect-the-element-that-is-receiving-the-focus
      const target = event.detail?.relatedTarget ?? null;
      if (!this.isTargetInsideComponent(target)) {
        this.hideAutoCompleteItems();
      }
    }
  }

  @HostListener('ionFocus', ['$event'])
  async onFocus() {
    if (this.showAllOnFocus) {
      this.loading = true;
      try {
        const results = await this.searchFn(this.searchValue ?? '');
        this.filteredItems = results;
      } finally {
        this.loading = false;
      }
      this.hideAutoCompleteItems$.next(false);
    } else if (this.filteredItems.length && this.searchValue) {
      this.hideAutoCompleteItems$.next(false);
    }
  }

  private static readonly ITEM_SIZE = 70;
  private static readonly MAX_VIEWPORT_HEIGHT = 300;

  activeSearchItemIndex = 0;

  private _filteredItems: InlineSearchItem[] = [];

  public get filteredItems(): InlineSearchItem[] {
    return this._filteredItems;
  }

  public set filteredItems(value: InlineSearchItem[]) {
    this._filteredItems = value;
    this.updateVisibleItems();
  }

  loading = false;

  @ViewChild(CdkVirtualScrollViewport) private scrollViewport?: CdkVirtualScrollViewport;

  visibleItems$ = new BehaviorSubject<InlineSearchItem[]>([]);

  viewportHeight$ = this.visibleItems$.pipe(
    map(items => Math.min(
      items.length * InlineSearchComponent.ITEM_SIZE,
      InlineSearchComponent.MAX_VIEWPORT_HEIGHT
    ))
  );

  searchValue: string | null;

  hideAutoCompleteItems$ = new BehaviorSubject(true);

  constructor(private elementRef: ElementRef<HTMLElement>) { }

  selectItemFromPointer(event: Event, searchItem: InlineSearchItem) {
    event.preventDefault();
    event.stopPropagation();

    this.selectItem(searchItem);
  }

  selectItem(searchItem: InlineSearchItem) {
    this.itemSelected.emit(searchItem);

    this.hideAutoCompleteItems();

    this.searchValue = '';
  }

  private updateVisibleItems(): void {
    const visible = this.filteredItems.filter(item => !this.excludeItemIds?.has(item.id));
    this.visibleItems$.next(visible);
  }

  ngOnInit() {

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['excludeItemIds']) {
      this.updateVisibleItems();
    }
  }

  async searchValueChange(query: string) {
    this.searchValue = query;
    if (isEmpty(query) && !this.showAllOnFocus) {
      this.filteredItems = [];
    } else {
      this.loading = true;

      const results = await this.searchFn(query);

      this.filteredItems = results;

      this.loading = false;
    }

    this.hideAutoCompleteItems$.next(false);
  }

  ionItemArrowDown(event: Event) {
    event.preventDefault();
    if (this.activeSearchItemIndex + 1 >= this.visibleItems$.value.length) {
      this.activeSearchItemIndex = 0;
    } else {
      this.activeSearchItemIndex++;
    }

    this.scrollFocusedItemIntoView();
  }

  ionItemArrowUp(event: Event) {
    event.preventDefault();
    if (this.activeSearchItemIndex - 1 < 0) {
      this.activeSearchItemIndex = this.visibleItems$.value.length - 1;
    } else {
      this.activeSearchItemIndex--;
    }

    this.scrollFocusedItemIntoView();
  }

  private hideAutoCompleteItems() {
    this.hideAutoCompleteItems$.next(true);
    this.activeSearchItemIndex = 0;
  }

  private isTargetInsideComponent(target: EventTarget | null) {
    return target instanceof HTMLElement && this.elementRef.nativeElement.contains(target);
  }

  scrollFocusedItemIntoView() {
    this.scrollViewport?.scrollToIndex(this.activeSearchItemIndex, 'instant');
  }
}
